import React, { useEffect, useRef, useState } from "react";
import TextArea from "antd/es/input/TextArea";
import Button from "components/button";
import { CircleStop, Send } from "lucide-react";
import ChatMessageView from "./ChatMessageView";

/**
 * The conversation itself: the transcript, the thinking state and the composer.
 *
 * Presentational and surface-agnostic. The floating bubble and the full Eli page
 * both render this, which is the whole point of the extraction: without it the
 * page would have a transcript and no way to type into it.
 *
 * Deliberately NOT here:
 *   - The New Chat control. It is a header icon in the bubble and a sidebar button
 *     on the page. Both dispatch the same redux action; only the control differs.
 *   - The Escape handler. Escape closes the floating panel, and a page has nothing
 *     to close.
 */
export const EliConversation = ({
    responseData,
    requesting = false,
    onSend,
    onRetry = () => {},
    messagesClassName = "",
    inputRef,
}) => {
    const [chatMessage, setChatMessage] = useState('');
    const chatEndRef = useRef(null);

    // The bubble's effect also depended on showChatEli, because the list stayed
    // mounted behind a hidden panel. Here the panel mounts this component when it
    // opens, so the mount run of this very effect is what scrolls on open.
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [responseData]);

    const submit = () => {
        // BUG-027: sends run with takeLatest (saga.jsx), so a send while Eli is
        // thinking would cancel the running answer. Wait for it to finish.
        if (chatMessage !== '' && !requesting) {
            onSend(chatMessage);
            setChatMessage('');
        }
    };

    return (
        <>
            <div data-testid="eli-messages" className={`overflow-auto py-2 mt-3 ${messagesClassName}`}>
                {responseData?.map((chat, index) => (
                    <ChatMessageView key={index} chat={chat} onRetry={onRetry} />
                ))}
                {requesting && (
                    <div className="px-2 py-1 text-sm text-gray-400 italic">Eli is thinking...</div>
                )}
                <div ref={chatEndRef} />
            </div>
            <div className="pt-4 flex gap-2">
                <TextArea
                    ref={inputRef}
                    className="input-field"
                    placeholder="Ask me anything..."
                    rows={1}
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            submit();
                        }
                    }}
                />
                {/* BUG-027: while Eli is thinking a send is blocked, so the button
                    says so: disabled, red, a stop icon, and a label for screen readers. */}
                <Button
                    data-testid="eli-send"
                    disabled={requesting}
                    aria-label={requesting ? "Eli is thinking. Wait for the answer to send." : "Send"}
                    title={requesting ? "Eli is thinking. Wait for the answer to send." : "Send"}
                    className={requesting
                        ? "flex items-center justify-center gap-2 border border-red-700 rounded-2xl p-3 text-[16px] text-white font-medium bg-red-600 cursor-not-allowed transition"
                        : "flex items-center justify-center gap-2 border border-gray-300 rounded-2xl p-3 text-[16px] text-white font-medium bg-gradient-to-br from-emerald-500 to-purple-600 hover:bg-gray-50 transition"}
                    onClick={submit}>
                    {requesting ? <CircleStop className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                </Button>
            </div>
        </>
    );
};

export default EliConversation;
